import { useMemo, useState, useRef, useEffect } from "react";
import { Check, ChevronDown, Layers, Search, Sparkles, X, Wallet, ArrowRight } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { EventImage } from "@/components/EventImage";
import { cn, formatBRL } from "@/lib/utils";

export interface WithdrawalEventOption {
  event_id: string;
  event_title: string;
  event_status?: string;
  event_end?: string | null;
  cover_image_url?: string | null;
  start_date?: string | null;
  available: number;
  gross_total?: number;
  pending_withdrawal?: number;
}

interface WithdrawalEventPickerProps {
  value: string;
  onValueChange: (eventId: string) => void;
  events: WithdrawalEventOption[];
  totalAvailable: number;
  className?: string;
}

export function WithdrawalEventPicker({
  value,
  onValueChange,
  events,
  totalAvailable,
  className,
}: WithdrawalEventPickerProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedEvent = useMemo(
    () => events.find((e) => e.event_id === value),
    [events, value]
  );

  const eventsWithBalanceCount = useMemo(
    () => events.filter((e) => Number(e.available) > 0).length,
    [events]
  );

  const [filterMode, setFilterMode] = useState<"all" | "with-balance">("with-balance");

  const filteredEvents = useMemo(() => {
    let list = [...events];
    if (filterMode === "with-balance") {
      list = list.filter((e) => Number(e.available) > 0);
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      list = list.filter((e) =>
        e.event_title.toLowerCase().includes(query)
      );
    }
    // Prioritize events with available balance at the top (highest balance first)
    return list.sort((a, b) => {
      const balA = Number(a.available || 0);
      const balB = Number(b.available || 0);
      if (balA > 0 && balB <= 0) return -1;
      if (balB > 0 && balA <= 0) return 1;
      if (balA > 0 && balB > 0) return balB - balA;
      return (a.event_title || "").localeCompare(b.event_title || "");
    });
  }, [events, searchQuery, filterMode]);

  // Focus search input when popover opens
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    } else {
      setSearchQuery("");
      setFilterMode(eventsWithBalanceCount > 0 ? "with-balance" : "all");
    }
  }, [open, eventsWithBalanceCount]);

  const handleSelect = (id: string) => {
    onValueChange(id);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-expanded={open}
          className={cn(
            "group flex min-h-[64px] w-full sm:w-auto sm:min-w-[380px] md:min-w-[420px] max-w-full sm:max-w-xl items-center justify-between gap-3 rounded-[24px] border border-border/60 bg-white dark:bg-card px-3.5 sm:px-4 py-3 text-left text-sm shadow-2xs transition-all duration-200 ease-out",
            "hover:border-border hover:bg-secondary/30 active:scale-[0.99]",
            "focus-visible:outline-none focus:outline-none focus-visible:ring-1 focus-visible:ring-border/80",
            className
          )}
        >
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {value === "all" ? (
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-secondary/70 text-foreground border border-border/40 transition-transform duration-200 group-hover:scale-105">
                <Layers className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
              </span>
            ) : (
              <div className="relative h-11 w-11 sm:h-12 sm:w-12 shrink-0 rounded-2xl overflow-hidden border border-border/50 shadow-2xs bg-muted transition-transform duration-200 group-hover:scale-105">
                <EventImage
                  src={selectedEvent?.cover_image_url}
                  alt={selectedEvent?.event_title || "Evento"}
                  className="h-full w-full object-cover"
                />
                {Number(selectedEvent?.available || 0) > 0 && (
                  <span className="absolute bottom-1 right-1 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-card" />
                )}
              </div>
            )}

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="truncate font-semibold text-foreground text-sm sm:text-[15px] leading-tight" title={value === "all" ? "Todos os eventos" : selectedEvent?.event_title}>
                  {value === "all"
                    ? "Todos os eventos"
                    : selectedEvent?.event_title || "Evento selecionado"}
                </p>
                {value === "all" && (
                  <Badge
                    variant="secondary"
                    className="h-5 rounded-full px-2 text-[10px] font-normal text-muted-foreground shrink-0 border-transparent bg-secondary"
                  >
                    {events.length} {events.length === 1 ? "evento" : "eventos"}
                  </Badge>
                )}
              </div>
              <p className="truncate text-xs text-muted-foreground mt-0.5">
                {value === "all" ? (
                  <span>Visão consolidada de todas as vendas</span>
                ) : (
                  <span>
                    Disponível para saque:{" "}
                    <span className="font-semibold text-emerald-600 dark:text-emerald-400 tabular-nums">
                      {formatBRL(Number(selectedEvent?.available || 0))}
                    </span>
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {value !== "all" && (
              <Badge
                variant="outline"
                className="h-5 rounded-full px-2 text-[10px] font-medium border-border/60 bg-secondary/50 text-foreground hidden min-[440px]:inline-flex"
              >
                Filtrado
              </Badge>
            )}
            <ChevronDown
              className={cn(
                "h-4 w-4 text-muted-foreground transition-transform duration-200 ease-out",
                open && "rotate-180 text-foreground"
              )}
            />
          </div>
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={8}
        className={cn(
          "w-[calc(100vw-1.5rem)] sm:w-[440px] max-w-[calc(100vw-1.5rem)] sm:max-w-xl p-2.5 sm:p-3",
          "rounded-[24px] border border-border/70 bg-white dark:bg-[#18181b] shadow-2xl",
          "animate-in fade-in-0 zoom-in-95 duration-150 ease-out"
        )}
      >
        {/* Apple-style Search Bar */}
        <div className="space-y-2">
          <div className="relative flex h-11 sm:h-10 items-center rounded-xl bg-secondary/50 px-3 transition-all focus-within:bg-secondary/70 focus-within:ring-1 focus-within:ring-border/80 border border-transparent">
            <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground/70" />
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Pesquisar evento pelo nome..."
              className="h-full w-full bg-transparent text-base sm:text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-0 outline-none border-none ring-0"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="flex h-7 w-7 sm:h-5 sm:w-5 items-center justify-center rounded-full bg-muted text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Limpar pesquisa"
              >
                <X className="h-3.5 w-3.5 sm:h-3 sm:w-3" />
              </button>
            )}
          </div>

          {/* Quick Filter Segmented Pills */}
          <div className="inline-flex items-center p-1 rounded-full bg-secondary/50 border border-border/40 gap-1 w-full">
            <button
              type="button"
              onClick={() => setFilterMode("with-balance")}
              className={cn(
                "flex items-center justify-center gap-1.5 h-9 sm:h-7 rounded-full px-3 text-xs sm:text-[11px] font-medium transition-all duration-150 ease-out flex-1 active:scale-[0.98]",
                filterMode === "with-balance"
                  ? "bg-white dark:bg-card text-foreground font-semibold shadow-2xs"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Wallet className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
              <span>Com saldo ({eventsWithBalanceCount})</span>
            </button>
            <button
              type="button"
              onClick={() => setFilterMode("all")}
              className={cn(
                "flex items-center justify-center h-9 sm:h-7 rounded-full px-3 text-xs sm:text-[11px] font-medium transition-all duration-150 ease-out flex-1 active:scale-[0.98]",
                filterMode === "all"
                  ? "bg-white dark:bg-card text-foreground font-semibold shadow-2xs"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              Todos ({events.length})
            </button>
          </div>
        </div>

        {/* List of Options */}
        <div className="mt-2.5 max-h-[300px] overflow-y-auto space-y-1 pr-0.5 scrollbar-thin">
          {/* "Todos os eventos" Row (only show when not in "with-balance" filter or if query matches) */}
          {filterMode === "all" &&
            (!searchQuery || "todos os eventos".includes(searchQuery.toLowerCase())) && (
              <div
                role="button"
                tabIndex={0}
                onClick={() => handleSelect("all")}
                onKeyDown={(e) => e.key === "Enter" && handleSelect("all")}
                className={cn(
                  "group flex cursor-pointer items-center justify-between gap-3 rounded-2xl p-2.5 transition-all duration-150 ease-out",
                  value === "all"
                    ? "bg-secondary/70 shadow-2xs"
                    : "hover:bg-secondary/40"
                )}
              >
                <div className="flex items-center gap-3 min-w-0">
                  {/* Apple tactile circular check */}
                  <div
                    className={cn(
                      "flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-full border transition-all duration-150 ease-out",
                      value === "all"
                        ? "border-primary bg-primary text-white shadow-2xs"
                        : "border-border/70 bg-transparent group-hover:border-muted-foreground/60"
                    )}
                  >
                    {value === "all" && <Check className="h-2.5 w-2.5 stroke-[3]" />}
                  </div>

                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-secondary/70 text-foreground border border-border/40">
                    <Layers className="h-5 w-5 text-muted-foreground" />
                  </div>

                  <div className="min-w-0">
                    <p className="font-medium text-xs sm:text-sm text-foreground leading-tight">
                      Todos os eventos
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      Visão consolidada ({events.length} {events.length === 1 ? "evento" : "eventos"})
                    </p>
                  </div>
                </div>

                <div className="text-right shrink-0">
                  <p className="text-xs font-semibold tabular-nums text-foreground">
                    {formatBRL(totalAvailable)}
                  </p>
                  <p className="text-[10px] text-muted-foreground">total</p>
                </div>
              </div>
            )}

          {/* Section Divider */}
          {filterMode === "all" &&
            (!searchQuery || "todos os eventos".includes(searchQuery.toLowerCase())) &&
            filteredEvents.length > 0 && (
              <div className="my-1.5 flex items-center gap-2 px-1">
                <div className="h-px flex-1 bg-border/40" />
                <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground/70">
                  Eventos individuais
                </span>
                <div className="h-px flex-1 bg-border/40" />
              </div>
            )}

          {/* Event Items */}
          {filteredEvents.length === 0 ? (
            <div className="py-8 text-center px-4">
              <p className="text-xs font-medium text-foreground">Nenhum evento encontrado</p>
              <p className="text-[11px] text-muted-foreground mt-1">
                {searchQuery
                  ? `Nenhum resultado para "${searchQuery}"`
                  : "Nenhum evento com saldo disponível para saque no momento."}
              </p>
              {(searchQuery || filterMode !== "all") && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    setFilterMode("all");
                  }}
                  className="mt-3 inline-flex items-center gap-1 rounded-full bg-secondary/80 px-3 py-1 text-[11px] font-medium text-foreground hover:bg-secondary transition-colors"
                >
                  Limpar filtros
                </button>
              )}
            </div>
          ) : (
            filteredEvents.map((event) => {
              const isSelected = value === event.event_id;
              const hasBalance = Number(event.available) > 0;

              return (
                <div
                  key={event.event_id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSelect(event.event_id)}
                  onKeyDown={(e) => e.key === "Enter" && handleSelect(event.event_id)}
                  className={cn(
                    "group flex cursor-pointer items-center justify-between gap-3 rounded-2xl p-2.5 transition-all duration-150 ease-out",
                    isSelected
                      ? "bg-secondary/90 shadow-xs"
                      : "hover:bg-secondary/50"
                  )}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {/* Apple tactile circular check */}
                    <div
                      className={cn(
                        "flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-full border transition-all duration-150 ease-out",
                        isSelected
                          ? "border-primary bg-primary text-white shadow-2xs"
                          : "border-border/70 bg-transparent group-hover:border-muted-foreground/60"
                      )}
                    >
                      {isSelected && <Check className="h-2.5 w-2.5 stroke-[3]" />}
                    </div>

                    {/* Event Cover Image Thumbnail */}
                    <div className="relative h-10 w-10 sm:h-11 sm:w-11 shrink-0 rounded-2xl overflow-hidden border border-border/50 bg-muted shadow-2xs">
                      <EventImage
                        src={event.cover_image_url}
                        alt={event.event_title}
                        className="h-full w-full object-cover"
                      />
                    </div>

                    <div className="min-w-0 flex-1">
                      <p
                        className={cn(
                          "font-medium text-xs sm:text-sm leading-snug line-clamp-1",
                          isSelected ? "text-foreground font-semibold" : "text-foreground/90"
                        )}
                        title={event.event_title}
                      >
                        {event.event_title}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[11px] text-muted-foreground">
                          {hasBalance ? "Disponível para saque:" : "Sem saldo para saque"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Value / Balance */}
                  <div className="text-right shrink-0">
                    <span
                      className={cn(
                        "inline-block text-xs font-semibold tabular-nums px-2 py-0.5 rounded-full transition-colors",
                        hasBalance
                          ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-bold"
                          : "text-muted-foreground/60"
                      )}
                    >
                      {formatBRL(Number(event.available))}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Apple-style Footer Hint */}
        <div className="mt-2 pt-2 border-t border-border/40 px-1 flex items-center justify-between text-[11px] text-muted-foreground">
          <span>Selecione um evento para solicitar saque</span>
          {value !== "all" && (
            <button
              type="button"
              onClick={() => handleSelect("all")}
              className="inline-flex items-center gap-1 font-medium text-foreground hover:underline"
            >
              Ver todos <ArrowRight className="h-3 w-3" />
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
